const blogPosts = [
  {
    slug: 'hosting-intimate-community-events',
    title: 'How to host intimate community events that people remember',
    date: '2024-12-08',
    author: 'Priyanka Roychoudhury',
    readTime: '6 min read',
    heroImage: '/blog/event-gathering.jpg',
    excerpt:
      'A step-by-step playbook for independent venues and organisers who want to design warm, human-centred gatherings without breaking the bank.',
    tags: ['Community', 'Event Planning', 'Venues'],
    intro:
      'Community events are one of the most powerful ways to showcase your venue, activate quiet hours, and build lasting relationships with guests. Here is the playbook we use at Occasia when we help organisers bring their ideas to life.',
    sections: [
      {
        heading: 'Start With A Purpose',
        paragraphs: [
          'Before logistics, get crystal clear on why this gathering matters. Are you launching a new menu, spotlighting local creatives, or bringing founders together? Name the goal, because it will influence everything from the invite copy to the format.',
          'A good test is to finish this sentence in one breath: “We’re bringing people together so that…”. If you can share that intention with your host team, you are ready to move into planning mode.'
        ]
      },
      {
        heading: 'Design The Experience',
        listTitle: 'Build the run-of-show around moments that matter:',
        listItems: [
          'Kick-off that sets the tone – create a warm arrival moment, even a simple greeting ritual.',
          'A hero segment – the activity, panel, tasting, or workshop guests are excited about.',
          'Ambient connection – curated seating, ice-breaker cards, or post-event hangouts so people stay longer.'
        ],
        paragraphs: [
          'Independent venues excel at making spaces feel personal. Use lighting, playlists, and signage to reflect the flavour of the night. Occasia hosts often add a “host corner” with venue stories, signature drinks, and postcards to encourage repeat visits.'
        ]
      },
      {
        heading: 'Keep Operations Lightweight',
        paragraphs: [
          'Smaller events thrive on simplicity. Lock in a checklist for the week of the event: catering confirmations, AV needs, floor-plan, and a single point of contact for the venue. We recommend a 30-minute pre-brief with suppliers and volunteer hosts.'
        ],
        listTitle: 'On the day, focus on three things:',
        listItems: [
          'Welcome – someone at the door, always.',
          'Flow – keep time with subtle cues, not frantic announcements.',
          'Follow-up – capture photos, quotes, and feedback while the energy is high.'
        ],
        paragraphs2: [
          'If you do this well, the story of the night continues online and positions your venue as the go-to spot for thoughtful gatherings.'
        ]
      }
    ],
    closing:
      'Want help hosting your next community gathering? The Occasia team can pair you with organisers looking for exactly your type of space. We’ll help you turn quiet evenings into memories – and repeat bookings.',
    cta: {
      label: 'List your venue on Occasia',
      link: '/venues-info'
    }
  },
  {
    slug: 'pricing-your-venue-for-weekday-hire',
    title: 'How to price your venue for weekday hire without guesswork',
    date: '2025-01-20',
    author: 'Hrishikesh Sarma',
    readTime: '7 min read',
    heroImage: '/blog/cafe-interior.jpg',
    excerpt:
      'Most venues know weekends sell themselves – the real question is how to monetise Mondays to Thursdays. Here’s a data-backed framework we use when setting rates for venues on Occasia.',
    tags: ['Revenue', 'Pricing', 'Hospitality'],
    intro:
      'Weekday bookings are gold. They can cover fixed costs, fill gaps between private hires, and introduce new communities to your space. But price too high and enquiries dry up; price too low and you leave money on the table. Use this framework to find your sweet spot.',
    sections: [
      {
        heading: 'Know Your Baseline',
        paragraphs: [
          'Start with your “quiet day” breakeven: operating cost per hour + team time + utilities. Add a margin for wear-and-tear and you have your absolute minimum.',
          'Next, check comparable venues. On Occasia we use a combination of public price boards and anonymised booking data to benchmark venues by location, capacity, and amenities.'
        ]
      },
      {
        heading: 'Package For Different Use Cases',
        paragraphs: [
          'Not all weekday bookings are created equal. Segment your pricing for at least three personas: team offsites, creator shoots, and community events. Each has different dwell times and add-ons.',
          'Bundle what you already have: screen, audio, coffee station, storage. Transparent packages reduce negotiation loops and make it easier for organisers to say yes.'
        ],
        listTitle: 'Example packages we see working well:',
        listItems: [
          'Morning Focus Session (9am–1pm) – includes hot drinks and AV setup.',
          'Creator Afternoon (1pm–5pm) – includes flexible furniture layout and daylight guarantee.',
          'Evening Social (6pm–10pm) – includes bar staff, signature drink, and clean down.'
        ]
      },
      {
        heading: 'Iterate With Data',
        paragraphs: [
          'Track enquiries, conversions, and actual utilisation. If a slot is at <40% occupancy for two months, test a promotional bundle or partner with a community host. If you’re consistently oversubscribed, nudge pricing up or introduce a minimum spend.',
          'At Occasia we review venue pricing every quarter. We combine seasonality trends (busy springs, slower Augusts) with behavioural data to keep rates competitive but fair.'
        ]
      }
    ],
    quote: {
      text: '“We filled three weekday evenings simply by packaging studio time with a hosted tasting. Priceless word-of-mouth.”',
      author: 'Will, Founder of a Peckham tasting room'
    },
    closing:
      'Pricing is never set-and-forget. When you treat weekdays as a product, you unlock an entirely new revenue stream. If you’d like personalised recommendations, our team can share the anonymised benchmarks we use when onboarding new venues.',
    cta: {
      label: 'Request a pricing review',
      link: 'mailto:hello@occasia.io?subject=Pricing%20review%20request'
    }
  },
  {
    slug: 'hidden-crisis-facing-local-community-venues',
    title: 'The hidden crisis facing local community venues - and why it matters',
    date: '2025-01-27',
    author: 'Occasia Team',
    readTime: '8 min read',
    heroImage: '/blog/pricing-desk.jpg',
    excerpt:
      'Local community venues are closing their doors faster than ever. Here\'s why this crisis matters - and what we can do about it.',
    tags: ['Community', 'Venues', 'Sustainability'],
    intro:
      'Local community venues have always been the quiet heartbeat of our neighbourhoods. Church halls, independent studios, community centres, creative spaces, small theatres, rehearsal rooms - these are places that feel familiar, human, and rooted in something deeper than convenience. But behind the scenes, a silent crisis is unfolding.',
    sections: [
      {
        heading: 'The Slow Disappearance of "Third Spaces"',
        paragraphs: [
          'Sociologists call them third spaces: not home, not work but the places where community happens.',
          'Yet many of these spaces are struggling in 2025 due to:'
        ],
        listItems: [
          'Rising energy and rent costs',
          'Irregular weekday bookings',
          'Missed or lost enquiries',
          'Reliance on outdated processes',
          'Limited marketing resources',
          'Competition from large commercial venue chains'
        ],
        paragraphs2: [
          'Most independent venue managers wear multiple hats - caretaker, bookkeeper, marketer, admin - and it\'s simply too much. The result is a slow decline that isn\'t always visible until it\'s too late.'
        ]
      },
      {
        heading: 'Why It Matters (More Than We Realise)',
        paragraphs: [
          'When small venues shut down, we lose:',
          '1. Spaces for Connection - Workshops, support groups, youth clubs, fitness classes, rehearsals - all rely on accessible local spaces. Without them, opportunities for belonging shrink.',
          '2. Cultural and Creative Ecosystems - Independent venues nurture grassroots art, music, craft, and cultural expression - the things that give places their identity.',
          '3. Affordable, Accessible Options - Large commercial venues often price out small organisers, charities, and local groups. Community venues bring equity to event hosting.',
          '4. Local Economies - Every booking supports a real person - not a corporation. More bookings mean more jobs and more community wealth.',
          '5. Everyday Joy - Celebrations, birthdays, baby showers, community meals - these don\'t need luxury halls. They need warm, local, meaningful places.'
        ]
      },
      {
        heading: 'How Technology Can Help - without Changing What Makes These Spaces Special',
        paragraphs: [
          'Independent venues don\'t need to become "tech companies." They just need simple, reliable tools that make their operations easier.',
          'Tools that allow them to:'
        ],
        listItems: [
          'Receive enquiries without missing them',
          'Showcase their space clearly online',
          'Fill underused hours',
          'Reduce admin',
          'Reach local organisers looking for exactly what they offer'
        ],
        paragraphs2: [
          'That\'s exactly what we\'re building at Occasia - a platform created for small venues and the communities they serve.',
          'Because when these spaces thrive, communities do too.'
        ]
      }
    ],
    closing:
      'Occasia exists because we believe real connection happens in real places. Our mission is to make small-scale event hosting simple, accessible, and sustainable while helping independent venues grow. The crisis facing local venues is real. But it\'s not irreversible. With the right visibility, tools, and support, these spaces can flourish again. And we\'re here to make that happen - one venue, one organiser, one community at a time.',
    cta: {
      label: 'List your venue on Occasia',
      link: '/venues-info'
    }
  },
  {
    slug: 'ultimate-guide-finding-small-event-venue',
    title: 'The ultimate guide to finding a small event venue without the stress',
    date: '2025-02-03',
    author: 'Occasia Team',
    readTime: '10 min read',
    heroImage: '/blog/empty-cafe.jpg',
    excerpt:
      'The average organiser spends 10+ hours searching for the perfect venue. Here\'s how to cut that time in half and find the right space without the stress.',
    tags: ['Event Planning', 'Venues', 'Guide'],
    intro:
      'If you\'ve ever tried to book a small event venue — a rehearsal room, studio, community hall, workshop space, classroom or meeting room — you already know: it\'s never as simple as it should be. The average organiser spends 10+ hours searching, emailing, comparing, and waiting for replies. And even after all that, you\'re often still unsure about cost, availability, and whether the space actually fits your needs. This guide exists to change that.',
    sections: [
      {
        heading: '1. Start With Your Event Needs (Not Just the Venue Type)',
        paragraphs: [
          'Before you browse venues, answer these essential questions:'
        ],
        listItems: [
          'How many people will attend?',
          'What vibe are you going for? (cosy, creative, professional, minimal)',
          'What equipment do you need? (projector, mirrors, kitchen, tables, mats)',
          'How accessible does the venue need to be? (wheelchair access, parking, public transport)',
          'What\'s your ideal location radius?'
        ],
        paragraphs2: [
          'Defining your needs early saves hours of back-and-forth later.'
        ]
      },
      {
        heading: '2. Prioritise Transparency: Look for Clear Pricing + Availability',
        paragraphs: [
          'One of the biggest frustrations organisers face is unclear costs.',
          'Hidden fees. Weekend surcharges. Deposit rules buried in PDFs. Availability calendars that don\'t reflect reality.',
          'Look for venues that offer:'
        ],
        listItems: [
          'Upfront hourly or daily pricing',
          'Clear availability',
          'Transparent rules (noise, catering, set-up time)',
          'No hidden extras'
        ],
        paragraphs2: [
          'This is one of the core reasons we built Occasia — so organisers can make confident decisions instantly.'
        ]
      },
      {
        heading: '3. Check Photos That Show the Space "In Use"',
        paragraphs: [
          'Photos matter. A lot.',
          'Look for:'
        ],
        listItems: [
          'Multiple angles',
          'Real events happening in the space',
          'Natural lighting',
          'Layout examples',
          'Measurements or floor plans'
        ],
        paragraphs2: [
          'Spaces that show themselves well online are usually easier to work with offline too.'
        ]
      },
      {
        heading: '4. Compare Not Just the Price — But the Value',
        paragraphs: [
          'The cheapest venue isn\'t always the best fit.',
          'Think about:'
        ],
        listItems: [
          'Equipment included',
          'Location convenience',
          'Set-up time',
          'Staff support',
          'Amenities',
          'Aesthetic / vibe',
          'Flexibility'
        ],
        paragraphs2: [
          'A venue that\'s £10/hour more expensive might save you hours in logistics.'
        ]
      },
      {
        heading: '5. Ask These 6 Questions Before You Book',
        paragraphs: [
          'Here\'s your organiser-approved checklist:'
        ],
        listItems: [
          'Is the timing flexible for set-up and clean-up?',
          'What\'s the cancellation policy?',
          'What equipment is included in the price?',
          'How does the venue handle noise or late hours?',
          'Is there on-site support during the event?',
          'Are there extra charges I should know about?'
        ],
        paragraphs2: [
          '(Pro tip: Occasia makes all of this transparent upfront so you don\'t need to chase!)'
        ]
      },
      {
        heading: '6. Make Use of Platforms That Simplify the Entire Process',
        paragraphs: [
          'Instead of: ❌ Searching 10+ websites ❌ Emailing dozens of venues ❌ Tracking replies manually',
          'Use a platform built for small event organisers.',
          'Platforms like Occasia let you:'
        ],
        listItems: [
          'See options instantly',
          'Compare prices and availability',
          'Discover unique community spaces you wouldn\'t find on Google',
          'Book and manage everything in one place'
        ],
        paragraphs2: [
          'Because venue hunting shouldn\'t take over your week.'
        ]
      }
    ],
    closing:
      'Whether you\'re hosting a workshop, rehearsal, celebration, class, or community meetup, the process should feel simple and inspiring. Small spaces have huge potential — and organising shouldn\'t get in the way of creativity, purpose, or connection. That\'s why we built Occasia: To give organisers their time back. To help communities connect more easily. And to make small events feel just as supported as big ones. Your next venue is out there. Let\'s help you find it — effortlessly.',
    cta: {
      label: 'Browse venues on Occasia',
      link: '/search'
    }
  }
];

export default blogPosts;

